// Colyseus client plumbing: join/rejoin the current area, forward state
// patches and chat into the world model, send intents up, and hop rooms
// when the server says a portal was crossed. No rendering concerns here.
//
// Every join first mints a one-time ticket from the cookie-authenticated
// HTTP API; the WebSocket side only ever sees that ticket. The server is
// the authority on WHICH room we belong in (/api/me → character.room).

import {
  type ArtifactUsedMessage,
  type ArtifactVerb,
  type BoltMessage,
  type ChatBroadcastMessage,
  type ChatSendMessage,
  type ClubJoinOptions,
  ClubState,
  type CombatMessage,
  type CombatVerb,
  type FireMessage,
  type FlyerTakenMessage,
  type FurnishHandleMessage,
  HomeState,
  type HomesteadReadMessage,
  type InputMessage,
  isKnownRoomId,
  type JobAcceptMessage,
  type JobCancelMessage,
  type JobDeliverMessage,
  type JobPostMessage,
  type JobResultMessage,
  KICKED_ELSEWHERE_CODE,
  type MarketFillMessage,
  type MarketPostMessage,
  type MarketResultMessage,
  type MarketWithdrawMessage,
  MessageType,
  type MountMessage,
  type PortalMessage,
  PROTOCOL_VERSION,
  type RouteMessage,
  resolvePlan,
  type SampleMessage,
  type SampleResultMessage,
  SpaceState,
  SURFACE_ID,
  SurfaceState,
  type SurveyMessage,
  type SurveyResultMessage,
  type ThreatMessage,
  type TradeOfferMessage,
  type TradeRequestMessage,
  type TradeRespondMessage,
  type TradeResultMessage,
  type UseArtifactMessage,
} from "@bebop/shared";
import { Client, type Room } from "@colyseus/sdk";
import type { World } from "./world";

const REJOIN_ATTEMPTS = 5;
const REJOIN_DELAY_MS = 1500;

/** Thrown when the session is gone — the flow router sends us to landing. */
export class NotSignedInError extends Error {}

async function mintTicket(): Promise<string> {
  const res = await fetch("/api/ws-ticket", { method: "POST" });
  if (res.status === 401 || res.status === 409) throw new NotSignedInError();
  if (!res.ok) throw new Error(`ws-ticket failed: ${res.status}`);
  const body = (await res.json()) as { ticket: string };
  return body.ticket;
}

/** The server's answer to "which room am I in?". */
async function currentRoom(): Promise<string> {
  const res = await fetch("/api/me");
  if (res.status === 401) throw new NotSignedInError();
  if (!res.ok) throw new Error(`/api/me failed: ${res.status}`);
  const body = (await res.json()) as { character: { room: string } | null };
  const room = body.character?.room;
  // Heal any room id without a live plan (stale renames) to the surface.
  // Home ids resolve too — their plans are synthesized from the id.
  return room && isKnownRoomId(room) ? room : SURFACE_ID;
}

export class NetClient {
  private client: Client;
  private room: Room<never, ClubState | SurfaceState | SpaceState | HomeState> | undefined;
  private closedByUs = false;
  /** Set while hopping rooms so the drop isn't treated as a failure. */
  private transitioningTo: string | undefined;

  /** Fired when a (re)join discovers the session is gone. */
  onSignedOut: (() => void) | undefined;
  /**
   * Fired the moment the server says a room hop is happening — BEFORE the
   * leave/join. The renderer uses it to start the transition wipe so the
   * destination is joined and warm behind full cover, never a load screen.
   */
  onTransition: ((from: string, to: string) => void) | undefined;

  constructor(
    private world: World,
    serverUrl: string,
  ) {
    this.client = new Client(serverUrl);
  }

  /** Join `roomId`, or wherever the server last saw our character. */
  async join(roomId?: string): Promise<void> {
    this.world.resetForJoin();
    this.world.status = this.world.status === "connecting" ? "connecting" : "reconnecting";

    const target = roomId ?? (await currentRoom());
    const options: ClubJoinOptions = {
      ticket: await mintTicket(),
      protocolVersion: PROTOCOL_VERSION,
    };
    // The decoder needs the right state class for the room kind. Mirrors
    // the server's routing rule exactly (the surface gets PlanetRoom, space
    // SpaceRoom, home interiors HomeRoom, every other area AreaRoom), so a
    // new interior kind cannot silently decode against the wrong schema.
    const kind = resolvePlan(target).kind;
    const stateClass =
      kind === "surface"
        ? SurfaceState
        : kind === "space"
          ? SpaceState
          : kind === "home"
            ? HomeState
            : ClubState;
    const room = await this.client.joinOrCreate<ClubState | SurfaceState | SpaceState | HomeState>(
      target,
      options,
      stateClass,
    );
    this.room = room;
    this.world.selfId = room.sessionId;
    this.world.roomId = target;

    room.onStateChange((state) => this.world.applyState(state, performance.now()));
    room.onMessage<ChatBroadcastMessage>(MessageType.chatMessage, (m) => this.world.addChat(m));
    room.onMessage<PortalMessage>(MessageType.portal, (m) => {
      this.onTransition?.(this.world.roomId, m.to);
      void this.transition(m.to);
    });
    room.onMessage<SurveyResultMessage>(MessageType.surveyResult, (m) => {
      this.world.onSurveyResult?.(m);
    });
    room.onMessage<SampleResultMessage>(MessageType.sampleResult, (m) => {
      this.world.onSampleResult?.(m);
    });
    room.onMessage<TradeRequestMessage>(MessageType.tradeRequest, (m) => {
      this.world.onTradeRequest?.(m);
    });
    room.onMessage<TradeResultMessage>(MessageType.tradeResult, (m) => {
      this.world.onTradeResult?.(m);
    });
    room.onMessage<FlyerTakenMessage>(MessageType.flyerTaken, (m) => {
      this.world.onFlyerTaken?.(m);
    });
    room.onMessage<BoltMessage>(MessageType.bolt, (m) => {
      this.world.onBolt?.(m);
    });
    room.onMessage<ArtifactUsedMessage>(MessageType.artifactUsed, (m) => {
      this.world.onArtifactUsed?.(m);
    });
    room.onMessage<ThreatMessage>(MessageType.threat, (m) => {
      this.world.onThreat?.(m);
    });
    room.onMessage<MarketResultMessage>(MessageType.marketResult, (m) => {
      this.world.onMarketResult?.(m);
    });
    room.onMessage<JobResultMessage>(MessageType.jobResult, (m) => {
      this.world.onJobResult?.(m);
    });
    room.onLeave((code?: number) => {
      this.room = undefined;
      if (code === KICKED_ELSEWHERE_CODE) {
        // Signed in elsewhere: never auto-rejoin (it would kick the newer
        // session right back, ping-ponging forever).
        this.world.status = "failed";
        console.warn("[net] disconnected: this account signed in elsewhere");
        return;
      }
      if (this.transitioningTo) return; // transition() drives the next join
      if (!this.closedByUs) void this.rejoin();
    });

    this.world.status = "connected";
  }

  /** Walked through a door: reconnect to the target room. */
  private async transition(to: string): Promise<void> {
    if (this.transitioningTo) return;
    this.transitioningTo = to;
    try {
      await this.room?.leave();
      await this.join(to);
    } catch (err) {
      if (err instanceof NotSignedInError) {
        this.onSignedOut?.();
        return;
      }
      console.error("[net] portal transition failed", err);
      this.transitioningTo = undefined;
      void this.rejoin();
      return;
    }
    this.transitioningTo = undefined;
  }

  sendInput(input: InputMessage): void {
    this.room?.send(MessageType.input, input);
  }

  sendMount(skiffId: string): void {
    const message: MountMessage = { skiffId };
    this.room?.send(MessageType.mount, message);
  }

  /** Reach for the pile by the door; the server decides what that means. */
  sendTakeFlyer(): void {
    this.room?.send(MessageType.takeFlyer, {});
  }

  sendDismount(): void {
    this.room?.send(MessageType.dismount, {});
  }

  /** A defensive verb: what the player TRIED; the server decides. */
  sendCombat(verb: CombatVerb, on?: boolean): void {
    const message: CombatMessage = on === undefined ? { verb } : { verb, on };
    this.room?.send(MessageType.combat, message);
  }

  /** Route the ship's aether pressure: burners, coils, veil, or even. */
  sendRoute(focus: string): void {
    const message: RouteMessage = { focus };
    this.room?.send(MessageType.route, message);
  }

  /** Fire the coil gun along a world-space aim direction. An intent —
   *  the server owns the cooldown, the charge, and the geometry. */
  sendFire(dx: number, dy: number, dz: number): void {
    const message: FireMessage = { dx, dy, dz };
    this.room?.send(MessageType.fire, message);
  }

  /** E at a stake or a shell: ask for the claim read aloud. */
  sendHomesteadRead(targetId: string): void {
    const message: HomesteadReadMessage = { targetId };
    this.room?.send(MessageType.homesteadRead, message);
  }

  /** E in your home: pick a piece up, or set the carried one down. */
  sendFurnishHandle(placeableId?: string): void {
    const message: FurnishHandleMessage = placeableId ? { placeableId } : {};
    this.room?.send(MessageType.furnishHandle, message);
  }

  sendSurvey(spawnId: string): void {
    const message: SurveyMessage = { spawnId };
    this.room?.send(MessageType.survey, message);
  }

  sendTradeOffer(offer: TradeOfferMessage): void {
    this.room?.send(MessageType.tradeOffer, offer);
  }

  /** Drink it, play it, wear it, shed it — the server decides and the
   *  room hears about the ones worth hearing about. */
  sendUseArtifact(artifactId: string, verb: ArtifactVerb): void {
    const message: UseArtifactMessage = { artifactId, verb };
    this.room?.send(MessageType.useArtifact, message);
  }

  sendMarketPost(message: MarketPostMessage): void {
    this.room?.send(MessageType.marketPost, message);
  }

  sendMarketFill(message: MarketFillMessage): void {
    this.room?.send(MessageType.marketFill, message);
  }

  sendMarketWithdraw(offerId: string): void {
    const message: MarketWithdrawMessage = { offerId };
    this.room?.send(MessageType.marketWithdraw, message);
  }

  sendJobPost(message: JobPostMessage): void {
    this.room?.send(MessageType.jobPost, message);
  }

  sendJobAccept(jobId: string): void {
    const message: JobAcceptMessage = { jobId };
    this.room?.send(MessageType.jobAccept, message);
  }

  sendJobDeliver(jobId: string, spawnId: string): void {
    const message: JobDeliverMessage = { jobId, spawnId };
    this.room?.send(MessageType.jobDeliver, message);
  }

  sendJobCancel(jobId: string): void {
    const message: JobCancelMessage = { jobId };
    this.room?.send(MessageType.jobCancel, message);
  }

  sendTradeRespond(offerId: string, accept: boolean): void {
    const message: TradeRespondMessage = { offerId, accept };
    this.room?.send(MessageType.tradeRespond, message);
  }

  sendSealReading(): void {
    this.room?.send(MessageType.sealReading, {});
  }

  sendSample(spawnId: string): void {
    const message: SampleMessage = { spawnId };
    this.room?.send(MessageType.sample, message);
  }

  sendChat(text: string): void {
    const message: ChatSendMessage = { text };
    this.room?.send(MessageType.chat, message);
  }

  leave(): void {
    this.closedByUs = true;
    void this.room?.leave();
  }

  private async rejoin(): Promise<void> {
    this.world.status = "reconnecting";
    for (let attempt = 1; attempt <= REJOIN_ATTEMPTS; attempt++) {
      await sleep(REJOIN_DELAY_MS * attempt);
      try {
        await this.join();
        return;
      } catch (err) {
        if (err instanceof NotSignedInError) {
          this.onSignedOut?.();
          return;
        }
        // A room that REFUSES us (barred) will refuse every attempt, and
        // burning all five leaves the player staring at "failed" with no
        // way back. /api/me already heals a barred room to the surface, so
        // one deliberate retry there is the recovery — not five at the
        // door that is shut.
        if (isRefusal(err) && this.world.roomId !== SURFACE_ID) {
          try {
            await this.join(SURFACE_ID);
            return;
          } catch {
            // fall through to the normal retry ladder
          }
        }
      }
    }
    this.world.status = "failed";
  }
}

/**
 * Did the server say no, as opposed to not answering? Colyseus surfaces an
 * onAuth throw as the message we threw, so the barred case is legible.
 */
function isRefusal(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? "");
  return /barred|not allowed|refused/i.test(message);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
